// src/belediye/components/halkMasasi/MaskedTimeline.js
//
//   ULTRA MINIMAL FEED renderer (Option A)
// - Tek container (.hm-tl-min-box)
// - Kutu/kart yok, ikon yok, badge yok, bold yok
// - Satır satır: [1] Başlık — sağda tarih
// - SMS satırı tıklanınca aynı satırın altında metin açılır (yeni kutu yok)
// - Final adımında citizen_note varsa ayrı satır altı metin olarak görünür
// - KVKK: actor/personel isimleri YOK
//
// Props:
// - items: [{ kind:"timeline"|"sms", title, at, type?, meta?, citizen_note?, message? }, ...]
// - loading, emptyText, resetKey
//
// Debug: REACT_APP_DEBUG_HALK_MASASI=1

import React, { useMemo, useState, useCallback, useEffect } from "react";
import { ensureFirstUpperTr, safeText } from "../utils/textFormat";
//   Paylaşılan tarih formatı (tek kaynak) — yerel Intl kopyası yerine
import { formatTrDateTimeSmart } from "../utils/TrDateTime";
import "./MaskedTimeline.css";

// ------------------------------------------------------------
// Debug
// ------------------------------------------------------------
const DEBUG = String(process.env.REACT_APP_DEBUG_HALK_MASASI || "").trim() === "1";
const log = (...args) => {
  if (DEBUG) console.log("[MaskedTimeline:min]", ...args);
};

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
const toText = (v) => String(v ?? "").trim();

const safeTime = (v) => {
  try {
    const t = new Date(v || 0).getTime();
    return Number.isNaN(t) ? 0 : t;
  } catch {
    return 0;
  }
};

const kindPriority = (kind) => {
  // aynı zamanda timeline önce, sms sonra
  if (kind === "timeline") return 0;
  if (kind === "sms") return 1;
  return 9;
};

const pickTitle = (x) => {
  const kind = toText(x?.kind) || (x?.message ? "sms" : "timeline");
  const raw = toText(x?.title) || (kind === "sms" ? "SMS gönderildi" : "İşlem");
  // minimal: başlık tek satır, normal weight
  return ensureFirstUpperTr(raw);
};

const pickAt = (x) => x?.at || x?.created_at || x?.sent_at || x?.date || null;

const pickCitizenNote = (x) => {
  if (x?.citizen_note !== undefined) return x.citizen_note;
  if (x?.citizenNote !== undefined) return x.citizenNote;
  return null;
};

const normalizeKind = (x) => {
  const k = toText(x?.kind);
  if (k === "sms" || k === "timeline") return k;
  if (toText(x?.message)) return "sms";
  return "timeline";
};

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
export default function MaskedTimeline({
  items = [],
  loading = false,
  emptyText = "Bu başvuru için kayıt bulunamadı.",
  resetKey = "",
}) {
  // SMS expand state (key -> open)
  const [openMap, setOpenMap] = useState({});

  useEffect(() => {
    setOpenMap({});
  }, [resetKey]);

  const toggleOpen = useCallback((k) => {
    setOpenMap((p) => ({ ...p, [k]: !p[k] }));
  }, []);

  const normalized = useMemo(() => {
    const arr = Array.isArray(items) ? items : [];

    const mapped = arr
      .map((x, idx) => {
        const kind = normalizeKind(x);
        const at = pickAt(x);
        if (!at) return null;

        const title = pickTitle(x);

        const message = kind === "sms" ? safeText(x?.message) : "";
        const hasMessage = Boolean(toText(message));

        const citizenNoteRaw = pickCitizenNote(x);
        const citizenNote =
          kind === "timeline" && toText(citizenNoteRaw)
            ? ensureFirstUpperTr(String(citizenNoteRaw))
            : "";

        return {
          _k: String(x?.id ?? `${kind}:${at}:${idx}`),
          kind,
          at,
          title,
          message,
          hasMessage,
          citizenNote,
        };
      })
      .filter(Boolean);

    // kronolojik ASC + kind priority
    mapped.sort((a, b) => {
      const ta = safeTime(a.at);
      const tb = safeTime(b.at);
      if (ta !== tb) return ta - tb;

      const ka = kindPriority(a.kind);
      const kb = kindPriority(b.kind);
      if (ka !== kb) return ka - kb;

      return String(a._k).localeCompare(String(b._k));
    });

    if (DEBUG) {
      log("normalized", {
        inLen: arr.length,
        outLen: mapped.length,
        sample: mapped[0] || null,
      });
    }

    return mapped;
  }, [items]);

  const isEmpty = !loading && normalized.length === 0;

  return (
    <section className="hm-tl-min" aria-label="Akış">
      <div className="hm-tl-min-box">
        {loading ? (
          <div className="hm-tl-min-row">
            <div className="hm-tl-min-no">…</div>
            <div className="hm-tl-min-main">
              <div className="hm-tl-min-title">Yükleniyor…</div>
            </div>
            <div className="hm-tl-min-date" />
          </div>
        ) : isEmpty ? (
          <div className="hm-tl-min-row">
            <div className="hm-tl-min-no">—</div>
            <div className="hm-tl-min-main">
              <div className="hm-tl-min-title">{emptyText}</div>
            </div>
            <div className="hm-tl-min-date" />
          </div>
        ) : (
          <ol className="hm-tl-min-list">
            {normalized.map((it, i) => {
              const isSms = it.kind === "sms";
              const isOpen = Boolean(openMap[it._k]);
              const isClickable = isSms && it.hasMessage;

              const onRowClick = isClickable ? () => toggleOpen(it._k) : undefined;

              return (
                <li
                  key={it._k}
                  className={`hm-tl-min-row ${isClickable ? "is-clickable" : ""}`}
                  onClick={onRowClick}
                  role={isClickable ? "button" : undefined}
                  aria-expanded={isClickable ? (isOpen ? "true" : "false") : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={
                    isClickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleOpen(it._k);
                          }
                        }
                      : undefined
                  }
                >
                  <div className="hm-tl-min-no">{i + 1}</div>

                  <div className="hm-tl-min-main">
                    <div className="hm-tl-min-title">
                      {it.title}
                      {/* opsiyonel mini ipucu: SMS satırı olduğunu küçükçe belirt */}
                      {isSms ? <span className="hm-tl-min-sub"> (SMS)</span> : null}
                    </div>

                    {/* SMS body (no box) */}
                    {isSms && isOpen && it.hasMessage ? (
                      <div className="hm-tl-min-body" style={{ whiteSpace: "pre-wrap" }}>
                        {it.message}
                      </div>
                    ) : null}

                    {/* Final citizen note (no box) */}
                    {!isSms && it.citizenNote ? (
  <div className="hm-tl-min-noteBox">
    <div className="hm-tl-min-note">{it.citizenNote}</div>
  </div>
) : null}
                  </div>

                  <div className="hm-tl-min-date">{formatTrDateTimeSmart(it.at)}</div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}